import type { OrderDetails } from '../services/orders.service';

export interface OrderDataRow {
  column: string;
  newValue: any;
  oldValue: any;
}

// Frontend field name (camelCase, as stored in editedOrderFields) → backend DB
// column (lowercase, as expected by the v1 /saveorderdetails endpoint). Add new
// editable order-level fields here and they will flow through both the modal
// and the ProDetail save paths automatically.
const ORDER_FIELD_COLUMNS: ReadonlyArray<readonly [keyof OrderDetails & string, string]> = [
  ['chassisType', 'chassistype'],
  ['chassisProvider', 'chassisprovider'],
  ['sealNumber', 'sealnumber'],
  ['chassisId', 'chassisid'],
  ['dropReleaseDatetime', 'dropreleasedatetime'],
  ['dropReleaseComment', 'dropreleasecomment'],
  // The one underscored column on the order tables (added 2026-09-08).
  ['bobtailApproved', 'bobtail_approved'],
  ['comments', 'comments'],
  ['dispatcher', 'dispatcher'],
  ['carrierCode', 'carriercode'],
  ['csr', 'csr'],
  ['whseCsrUsername', 'whsecsr'],
  ['container', 'container'],
  ['size', 'size'],
  ['equipType', 'equiptype'],
  ['isGwiEquip', 'isgwiequip'],
  ['isTank', 'istank'],
  ['isDaycab', 'isdaycab'],
  ['otr', 'otr'],
  ['scale', 'scale'],
  ['isDrop', 'isdrop'],
  ['rail', 'rail'],
  // dvirframe column is repurposed on the order for the Service Failure flag.
  ['serviceFailure', 'dvirframe'],
  ['flexBooking', 'flexbooking'],
  // Return equipment for Domestic moves (movetype='DOMESTIC'). Editable in the
  // Order Info tab; returnpronumber is numeric, the equip/chassis are free text.
  ['returnPronumber', 'returnpronumber'],
  ['returnEquipment', 'returnequipment'],
  ['returnChassis', 'returnchassis'],
  // Order Form editable fields (Task B1)
  ['commodity', 'commodity'],
  ['pieceCount', 'piececount'],
  ['lbs', 'lbs'],
  ['kgs', 'kgs'],
  ['metricTons', 'metrictons'],
  ['tareLbs', 'tarelbs'],
  ['purchaseOrder', 'po'],
  ['scac', 'scac'],
  // Order Info fields opened for LC editing (2026-07-22, display-config work).
  // Must be listed here or edits are silently dropped from the save payload.
  ['vessel', 'vessel'],
  ['voyage', 'voyage'],
  ['releasedSet', 'releasedset'],
  ['foodGrade', 'foodgrade'],
  ['offhire', 'offhire'],
  ['equipReposition', 'equipreposition'],
  ['tmwOrderNum', 'tmwordernum'],
  ['hot', 'hot'],
  ['swing', 'swing'],
  ['customerCutoff', 'customercutoff'],
  ['custDocCut', 'dvirlights'],
  ['bookingNumber', 'bol'],
  // Rolling ("Booking Hold") + its cascade targets. Editing rolling to POOL/
  // ROLLING clears firstRcv/cutoff/documentCutoff client-side; all five must be
  // in the save map so the cleared/changed values persist.
  ['rolling', 'rolling'],
  ['rollingFault', 'rollingfault'],
  ['firstRcv', 'firstrcvdate'],
  ['cutoffDatetime', 'cutoffdatetime'],
  ['documentCutoffDatetime', 'documentcutoffdatetime'],
  // Available-to-Ship stamp (LC checkbox stamps user + browser now; uncheck
  // clears both). Read-only displays otherwise.
  ['availToShipDatetime', 'availabletoshipdatetime'],
  ['availToShipUser', 'availabletoshipuser'],
  ['lastFreeDay', 'lastfreedaydate'],
  ['examLfd', 'examlfddate'],
  ['redeliveryExpirationDate', 'redeliveryexpirationdate'],
  ['etaDate', 'etadate'],
  // CST Dispatcher — the editable value is the username (cstdispatcher column);
  // cstDispatcher (display name) is derived at read time, not saved.
  ['cstDispatcherUsername', 'cstdispatcher'],
  // Rail-to-Port "Port Trace" terminal (same options as rail).
  ['portTrace', 'porttrace'],
  // Overweight approval fields — written by the realtime calc when the
  // computed isOverweight state changes the approval status (V1 parity:
  // "Exception weight limit exceeded, system approval removed").
  ['overweightApprovedBy', 'overweightapprovedby'],
  ['overweightApprovedDatetime', 'overweightapproveddatetime'],
  // Chassis Specs modal fields (Dimensions/OOG/Permits port). lengthInches/
  // widthInches/heightInches are the persisted raw inches; the modal's derived
  // ft+in/cm/Amount-OOG displays are never saved (matches V1's scratch fields).
  ['lengthInches', 'lengthinches'],
  ['widthInches', 'widthinches'],
  ['heightInches', 'heightinches'],
  ['oog', 'oog'],
  ['ooglegal', 'ooglegal'],
  ['brokerout', 'brokerout'],
  ['oversizePermitNumber', 'oversizepermitnumber'],
  ['overweightPermitNumber', 'overweightpermitnumber'],
  ['oversizePermitRequired', 'oversizepermitrequired'],
  ['overweightPermitRequired', 'overweightpermitrequired'],
  ['oversizeApprovedBy', 'oversizeapprovedby'],
  ['oversizeApprovedDatetime', 'oversizeapproveddatetime'],
  ['triaxle', 'triaxle'],
  // Template invoice/approval stamp — the "template approved" switch on the
  // Invoicing tab. approvedby = v1 username, approveddatetime = browser now
  // (client-stamped). cmsapi routes these to template.approvedby/approveddatetime
  // when the pronumber is a template.
  ['invoiceApprovedBy', 'approvedby'],
  ['invoiceApprovedDatetime', 'approveddatetime'],
  // Hazmat approval — the Addl Info "Approved?" checkbox stamps/clears the
  // current user here; cmsapi's generic orderData handler writes
  // ordersall.hazmatapprovedby (+ history). No datetime column: the read path
  // reconstructs approver/timestamp from cms.history. Server-side gated on the
  // `hazmat-approval` component permission in save_order_details.
  ['hazmatApprovedBy', 'hazmatapprovedby'],
  // Hazmat Y/N flag — LC-editable on the Order Info tab (order-lc-fields gate).
  // Also auto-managed by the hazmat line add/delete endpoints; this persists a
  // manual override through the main save.
  ['hazmat', 'hazmat'],
  // Railbill Y/N flag — dispatcher-editable on the Order Info tab (its own
  // railbill-flag component permission, separate from order-lc-fields).
  ['railbilled', 'railbilled'],
  // Reuse fields editable from AddlInfoView. reuseStatusUser /
  // reuseStatusDatetime are auto-stamped when reuseStatus changes (see
  // AddlInfoView's onChange) so they flow through the same save path.
  ['reuseStatus', 'reusestatus'],
  ['reuseOrigCode', 'reuseorigcode'],
  ['reuseStatusUser', 'reusestatususer'],
  ['reuseStatusDatetime', 'reusestatusdatetime'],
  // AddlInfo lookups — Consignee / Customer / Broker/Forwarder / Broker Contact.
  // ONLY the code (FK recno) is persisted; the *name* is derived at read time
  // via a JOIN (consigneeloc.lname / c.custname / bf.name / bc.contactname) and
  // is NOT a writable column — sending it makes v1's UPDATE fail with
  // "Unknown column 'brokername' in 'SET'". The picked name shows optimistically
  // from editedOrderFields and is re-derived on the post-save refetch.
  ['consigneeCode', 'consigneecode'],
  ['customer', 'customercode'],
  ['brokerCode', 'brokercode'],
  ['brokerContactRecno', 'brokercontactrecno'],
  // Bill To Contact (Invoicing tab) — only the contactinfo recno is writable;
  // the name/phone/email are derived at read time (JOIN), same as brokerContact.
  ['billToContactRecno', 'billtocontactrecno'],
  // AddlInfo editable fields (Task E1)
  ['packaged', 'packaged'],
  ['equipHold', 'equiphold'],
  ['streetTurnHold', 'streetturnhold'],
  // Street Turn Review reuses the `mule` column (Y blocks a terminate-leg driver).
  ['streetTurnReview', 'mule'],
  ['inbondType', 'inbondtype'],
  ['inbondNumber', 'inbondnumber'],
  ['yardReportDatetime', 'yardreportdatetime'],
  ['notOnYardReportDate', 'notonyardreportdate'],
  ['renderedDeadlineDate', 'rendereddeadlinedate'],
  ['renderedDate', 'rendereddate'],
  ['isExam', 'isexam'],
  ['examiner', 'examiner'],
  // Addl Info fields opened for LC editing (2026-07-22, field-by-field review).
  ['priority', 'priority'],
  ['priority2', 'priority2'],
  ['dcDueDate', 'dcduedate'],
  ['demurrageReason', 'demurragereason'],
  ['examNo', 'examno'],
  ['examReleaseDate', 'examreleasedate'],
  ['inbondResponsible', 'inbondresponsible'],
  ['inbondRespOther', 'inbondrespother'],
  ['inbondTenderRcvd', 'inbondtenderrcvd'],
  ['whseEntryNum', 'whseentrynum'],
  ['whse3461Date', 'whse3461date'],
  ['whseRedStampDate', 'whseredstampdate'],
  ['zeroRecordDate', 'zerorecorddate'],
  ['yardLocation', 'yardlocation'],
  ['fullEmptyDate', 'fullemptydate'],
  ['temperature', 'temperature'],
  ['temperatureControlled', 'temperaturecontrolled'],
  ['tempScale', 'tempscale'],
  ['ventSetting', 'ventsetting'],
  ['genset', 'genset'],
  ['highValueCargo', 'highvaluecargo'],
  ['cargoValue', 'cargovalue'],
  // Tracing editable fields (Task E2)
  ['traceRunDatetime', 'tracerundatetime'],
  ['traceRunCarrier', 'traceruncarrier'],
  ['traceEventDatetime', 'traceeventdatetime'],
  ['traceEvent', 'traceevent'],
  ['traceLocation', 'tracelocation'],
  ['revisedEtaDatetime', 'revisedetadatetime'],
  ['availableGoodToGoDatetime', 'availablegoodtogodatetime'],
  ['outGateDatetime', 'outgatedatetime'],
  ['traceTerminateDatetime', 'traceterminatedatetime'],
  ['customsStatus', 'customsstatus'],
  ['lineStatus', 'linestatus'],
  ['otherHolds', 'otherholds'],
  // Tracing Mode — the order's `mode` column (served as traceMode). Editable on
  // templates (State 3) and orders.
  ['traceMode', 'mode'],
  // Tracing tab field opens (order-tracing-tab batch). All LC-gated
  // (order-lc-fields). Their date/datetime columns are registered in
  // DATE_FIELDS / DATETIME_FIELDS below. NOTE: Trace Container is deliberately
  // absent — it's a tracing-only UI helper, never persisted to the order.
  ['traceVessel', 'tracevessel'],
  ['traceVoyage', 'tracevoyage'],
  ['vesselSailDateEst', 'vesselsaildateest'],
  ['vesselSailDatetime', 'vesselsaildatetime'],
  ['vesselArrivalDateEst', 'vesselarrivaldateest'],
  ['vesselArrivalDatetime', 'vesselarrivaldatetime'],
  ['trainDepartDatetime', 'traindepartdatetime'],
  ['arrivalGroundedDatetime', 'arrivalgroundeddatetime'],
  ['arrivalGroundedLocation', 'arrivalgroundedlocation'],
  ['bookingOnFileDate', 'bookingonfiledate'],
  ['equipReleaseDate', 'equipreleasedate'],
  ['customsRelDatetime', 'customsreldatetime'],
  ['preAdviseDatetime', 'preadvisedatetime'],
  ['railBillStatus', 'railbillstatus'],
  ['schedulingContactType', 'schedulingcontacttype'],
  ['schedulingContactInfo', 'schedulingcontactinfo'],
  ['railPickupContactType', 'railpickupcontacttype'],
  ['railPickupContactInfo', 'railpickupcontactinfo'],
  // THD LFD (rail last-free-day) — open-entry date, Home-Depot moves only.
  ['railLfdDate', 'raillfddate'],
  // AddlInfo · Audit & Reuse — template-editable fields (FIELD_AUDIT #4).
  // `allowBrokerage` is the repurposed `dvirwheel` column (read at orders.py
  // :2999) — write it back to the same column so the round-trip is consistent.
  ['allowBrokerage', 'dvirwheel'],
  ['masterPro', 'masterpro'],
  ['moveType', 'movetype'],
  ['gwiMove', 'gwimove'],
  ['sendEmailNotifications', 'sendemailnotifications'],
  ['addlInfoDeleted', 'deleted'],
  // Rate Zone — persists the ratezone code to `templatetype` (State 2,
  // key-shape / re-quote trigger). Options from /quote-maint/rate-zones.
  ['templateType', 'templatetype'],
  // Ports (Order Info) — persist the cms.ports recno to portoforigin/
  // portofdischarge; the *Name fields are derived at read time (JOIN), so only
  // the code is writable (same pattern as consignee/broker).
  ['portOfOriginCode', 'portoforigin'],
  ['portOfDischargeCode', 'portofdischarge'],
  // DC / Dept (Addl Info). dcNumber saves the dc_map number (or free text when
  // the customer has no DCs); dept is always free text.
  ['dcNumber', 'dcnumber'],
  ['dept', 'dept'],
  // Target / Customer Target dates (Order Info) — LC-editable via the
  // order-lc-fields gate. targetOnOrBy is a plain 'ON'/'BY' string; the two
  // dates round-trip through DATE_FIELDS below. targetdeliverydate changes
  // also drive the order_targetdeliverydate saverule (deliverbydateAG EDI).
  ['targetOnOrBy', 'targetonorby'],
  ['targetDeliveryDate', 'targetdeliverydate'],
  ['custTargetDate', 'deliveryoption1date'],
  // Addl Cost — per-diem LC-editable fields (order-invoice-tab batch). Dates
  // round-trip through DATE_FIELDS below; the display PD amount is recomputed
  // server-side (perdiemamount is NOT written from here).
  ['perDiemStartDate', 'perdiemstartdate'],
  ['perDiemEndDate', 'perdiemenddate'],
  ['perDiemFollowupDate', 'perdiemfollowupdate'],
  ['perDiemContractNumber', 'perdiemcontractnumber'],
  ['perDiemApproval', 'perdiemapproval'],
  ['chassisFeeResolved', 'chassisfeeresolved'],
  ['chassisFeeResolvedComment', 'chassisfeeresolvedcomment'],
  // Invoicing-tab fields that live on ORDERSALL (not the invoice table). They ride
  // the order section of the save (orderData -> ordersall) in both cmsapi + native
  // modes; the invoice-TABLE fields go through invoiceData / the side-channel patch
  // instead. marginApprovedBy/Datetime are client-stamped by the Approve checkbox
  // (like the template approve toggle), so both round-trip here.
  ['cod', 'cod'],
  ['freightTerms', 'freightterms'],
  ['requirePerDiemApproval', 'requireperdiemapproval'],
  // Template-editable invoice fields that live on the order table (marginlimitlocal,
  // billto). Read-only on a real order's Invoicing tab today; editable on a template
  // (marginLimit State 3, billTo State 2). billTo persists only the bill-to recno —
  // the name/contact are read-derived (JOIN), same as consignee/broker.
  ['marginLimitLocal', 'marginlimitlocal'],
  ['billTo', 'billto'],
  ['marginApprovedBy', 'marginapprovedby'],
  ['marginApprovedDatetime', 'marginapproveddatetime'],
  ['rateComments', 'ratecomments'],
  ['rebill', 'rebill'],
  ['invoiceHoldReason', 'invoiceholdreason'],
  ['invoiceHoldFollowupDatetime', 'invoiceholdfollowupdatetime'],
  // Missing Inv Approved (Invoicing tab). The approver stamp IS the Y/N flag —
  // V1's `$miapproved = missinginvoiceapprovedby ? 'Y' : 'N'`, the same derivation
  // accessorial_service.apply_missing_inv_approval uses — so both surfaces read one
  // source of truth. missinginvoiceapproveddatetime is intentionally NOT mapped:
  // V1 never populated it (0 rows of 1.28M in prod).
  ['missingInvoiceApprovedBy', 'missinginvoiceapprovedby'],
  ['missingInvoiceApprovedReason', 'missinginvoiceapprovedreason'],
];

// V1 DATE columns — backend expects 'YYYY-MM-DD' (empty → '0000-00-00').
const DATE_FIELDS: ReadonlySet<string> = new Set([
  'examlfddate',
  'redeliveryexpirationdate',
  'rendereddeadlinedate',
  'rendereddate',
  'notonyardreportdate',
  'targetdeliverydate',
  'deliveryoption1date',
  // Tracing date-only columns (order-tracing-tab batch).
  'bookingonfiledate',
  'equipreleasedate',
  'raillfddate',
  // Addl Cost per-diem date columns (order-invoice-tab batch).
  'perdiemstartdate',
  'perdiemenddate',
  'perdiemfollowupdate',
]);

// V1 DATETIME columns — backend expects 'YYYY-MM-DD HH:MM:SS'
// (empty → '0000-00-00 00:00:00').
const DATETIME_FIELDS: ReadonlySet<string> = new Set([
  'yardreportdatetime',
  'tracerundatetime',
  'traceeventdatetime',
  'revisedetadatetime',
  'availablegoodtogodatetime',
  'outgatedatetime',
  'traceterminatedatetime',
  'overweightapproveddatetime',
  'oversizeapproveddatetime',
  'reusestatusdatetime',
  'approveddatetime',
  // Invoicing-tab ordersall datetimes.
  'marginapproveddatetime',
  'invoiceholdfollowupdatetime',
  // Available-to-Ship stamp (Order Info) — client-built 'MM/DD/YYYY HH:MM' string.
  'availabletoshipdatetime',
  // Tracing datetime columns (order-tracing-tab batch).
  'vesselsaildateest',
  'vesselsaildatetime',
  'vesselarrivaldateest',
  'vesselarrivaldatetime',
  'traindepartdatetime',
  'arrivalgroundeddatetime',
  'customsreldatetime',
  'preadvisedatetime',
]);

const EMPTY_DATE = '0000-00-00';
const EMPTY_DATETIME = '0000-00-00 00:00:00';

/**
 * Converts a UI date/datetime string into the V1 backend format.
 *
 * - Empty/null/undefined → '0000-00-00' (date) or '0000-00-00 00:00:00' (datetime)
 * - 'MM/DD/YYYY' → 'YYYY-MM-DD'
 * - 'MM/DD/YYYY HH:MM' → 'YYYY-MM-DD HH:MM:00'
 * - 'MM/DD HH:MM' / 'MM/DD' (year-less display form) → current year assumed
 * - Already-formatted 'YYYY-MM-DD' / 'YYYY-MM-DD HH:MM[:SS]' → pass through
 *   (datetime gets ':00' seconds appended if missing); a trailing timezone
 *   ('Z' / '±HH:MM') or fractional seconds are tolerated and dropped.
 *
 * The year-less display form matters for the OLD value on save: get_order_details
 * serves current-year datetimes as DATE_FORMAT('%m/%d %H:%i') (e.g. '08/17 00:00',
 * no year). Without reconstructing the year here, the old value collapsed to the
 * zero sentinel — so clearing a current-year date no-oped under the native save
 * (new == old → the column was dropped from the UPDATE) and history logged a bogus
 * '0000-00-00' old value (bugfix/#33251). The abbreviated form is only emitted when
 * the value's year IS the current year, so assuming the current year is exact.
 *
 * FUTURE (architectural cleanup): the current-year reconstruction only exists
 * because get_order_details formats these datetimes server-side (SQL DATE_FORMAT
 * → year-less 'MM/DD HH:MM'), a V1-parity carryover, so the frontend never
 * receives the full value to round-trip. The proper fix is to serve editable
 * date/datetime columns raw (ISO / full precision) and move display formatting to
 * the frontend, keeping the raw value for old-value round-tripping — which removes
 * BOTH the lossy round-trip and the current-year assumption. It's a larger,
 * cross-cutting refactor (every date column in the order-details SELECT + the
 * OrderDetails type + each display site), so it's tracked as a follow-up to
 * bugfix/#33251 rather than done here. Note: some columns (e.g. preadvisedatetime)
 * are ALREADY served raw — the layer is mid-migration and inconsistent.
 *
 * Falls back to the empty sentinel if the input cannot be parsed; we'd
 * rather clear a bad date than send garbage to V1.
 */
export function toBackendDateValue(
  value: unknown,
  isDatetime: boolean,
): string {
  const emptySentinel = isDatetime ? EMPTY_DATETIME : EMPTY_DATE;
  if (value == null) return emptySentinel;
  const trimmed = String(value).trim();
  if (!trimmed) return emptySentinel;
  // V1 itself uses these zero sentinels — pass them through.
  if (trimmed === EMPTY_DATE || trimmed === EMPTY_DATETIME) return emptySentinel;

  // Year to assume for the year-less display form the backend emits for
  // current-year values (DATE_FORMAT '%m/%d ...'). Matches the server's
  // YEAR(CURDATE()) except across a year-boundary clock skew — negligible, and
  // far better than zeroing the value.
  const thisYear = String(new Date().getFullYear());

  if (isDatetime) {
    // Already-backend datetime 'YYYY-MM-DD HH:MM[:SS]' — tolerate a trailing
    // 'Z'/offset or fractional seconds (raw-served datetimes serialize as ISO);
    // the wall-clock components ARE the stored naive value, so drop the zone.
    const isoDt = trimmed.match(
      /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?$/,
    );
    if (isoDt) {
      const [, y, mo, d, h, mi, se] = isoDt;
      return `${y}-${mo}-${d} ${h}:${mi}:${se ?? '00'}`;
    }
    // Date-only ISO with no time — pad to midnight.
    const isoDateOnly = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoDateOnly) return `${trimmed} 00:00:00`;
    // Display format: 'MM/DD/YYYY HH:MM[:SS]'
    const mdyDt = trimmed.match(
      /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/,
    );
    if (mdyDt) {
      const [, m, d, rawY, h, mi, se] = mdyDt;
      const y = rawY.length === 2 ? `20${rawY}` : rawY;
      return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')} ${h.padStart(2, '0')}:${mi}:${se ?? '00'}`;
    }
    // Year-less display 'MM/DD HH:MM[:SS]' (backend current-year form) — assume
    // this year. Must come before the bare-'MM/DD' check below.
    const mdDt = trimmed.match(/^(\d{1,2})\/(\d{1,2})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (mdDt) {
      const [, m, d, h, mi, se] = mdDt;
      return `${thisYear}-${m.padStart(2, '0')}-${d.padStart(2, '0')} ${h.padStart(2, '0')}:${mi}:${se ?? '00'}`;
    }
    // Display format: 'MM/DD/YYYY' with no time — pad to midnight.
    const mdyDate = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
    if (mdyDate) {
      const [, m, d, rawY] = mdyDate;
      const y = rawY.length === 2 ? `20${rawY}` : rawY;
      return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')} 00:00:00`;
    }
    // Year-less 'MM/DD' with no time (backend current-year form) — this year, midnight.
    const mdDate = trimmed.match(/^(\d{1,2})\/(\d{1,2})$/);
    if (mdDate) {
      const [, m, d] = mdDate;
      return `${thisYear}-${m.padStart(2, '0')}-${d.padStart(2, '0')} 00:00:00`;
    }
    return emptySentinel;
  }

  // DATE column.
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  // 'YYYY-MM-DD HH:MM[:SS]' on a date column — truncate to the date portion.
  const isoDtForDate = trimmed.match(/^(\d{4}-\d{2}-\d{2})[ T]\d{2}:\d{2}/);
  if (isoDtForDate) return isoDtForDate[1];
  const mdy = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (mdy) {
    const [, m, d, rawY] = mdy;
    const y = rawY.length === 2 ? `20${rawY}` : rawY;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  // Display datetime on a date column — keep the date portion only.
  const mdyDt = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})\s+\d{1,2}:\d{2}/);
  if (mdyDt) {
    const [, m, d, rawY] = mdyDt;
    const y = rawY.length === 2 ? `20${rawY}` : rawY;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  // Year-less 'MM/DD' (± time) on a date column (backend current-year form) —
  // assume this year, keep the date portion.
  const mdForDate = trimmed.match(/^(\d{1,2})\/(\d{1,2})(?:\s+\d{1,2}:\d{2})?$/);
  if (mdForDate) {
    const [, m, d] = mdForDate;
    return `${thisYear}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return emptySentinel;
}

/**
 * Looks up the backend DB column for a camelCase order field key (the same
 * map used by buildOrderDataRows/buildOrderChangeAllRows), e.g. for translating
 * a UI field name to the `colname` expected by GET /orders/history/{pro}.
 * Returns undefined for keys not tracked in ORDER_FIELD_COLUMNS.
 */
export function fieldKeyToColumn(field: string): string | undefined {
  const entry = ORDER_FIELD_COLUMNS.find(([key]) => key === field);
  return entry?.[1];
}

export function buildOrderDataRows(
  edited: Record<string, any>,
  current: Partial<OrderDetails>,
): OrderDataRow[] | undefined {
  const rows: OrderDataRow[] = [];
  for (const [field, column] of ORDER_FIELD_COLUMNS) {
    if (edited[field] !== undefined) {
      let newValue = edited[field];
      let oldValue = (current as any)[field] ?? '';
      const isDate = DATE_FIELDS.has(column);
      const isDatetime = DATETIME_FIELDS.has(column);
      if (isDate || isDatetime) {
        newValue = toBackendDateValue(newValue, isDatetime);
        oldValue = toBackendDateValue(oldValue, isDatetime);
      } else {
        oldValue = oldValue || '';
      }
      rows.push({ column, newValue, oldValue });
    }
  }
  return rows.length > 0 ? rows : undefined;
}

/**
 * Change-All variant: for each field the user checked "apply to all", emit its
 * {column, newValue} using the same field→column map + date normalization as
 * buildOrderDataRows, but WITHOUT oldValue (each target pro's current value
 * differs). `checked` holds camelCase field names.
 *
 * `current` is the anchor's SAVED order, and it makes flagging an UNCHANGED
 * field meaningful: a user copying the anchor's existing commodity to the rest
 * of the set has nothing to edit, so before this the checkbox produced no row
 * and the whole Change All looked broken. A flagged field now falls back to
 * what the anchor already holds. Omit `current` and the old edited-only
 * behaviour is unchanged.
 *
 * A field absent from BOTH is skipped — there is no value to send. A field the
 * anchor holds as blank is NOT skipped: "copy the anchor's value" includes
 * copying a blank one, and the review modal names every column before the user
 * commits.
 */
export function buildOrderChangeAllRows(
  edited: Record<string, any>,
  checked: ReadonlySet<string>,
  current?: Record<string, any> | null,
): { column: string; newValue: any }[] {
  const rows: { column: string; newValue: any }[] = [];
  for (const [field, column] of ORDER_FIELD_COLUMNS) {
    if (!checked.has(field)) continue;
    const edit = edited[field];
    let newValue = edit !== undefined ? edit : current?.[field];
    if (newValue === undefined) continue;
    const isDate = DATE_FIELDS.has(column);
    const isDatetime = DATETIME_FIELDS.has(column);
    if (isDate || isDatetime) newValue = toBackendDateValue(newValue, isDatetime);
    rows.push({ column, newValue });
  }
  return rows;
}
